import { AuthImage } from './AuthImage'
import { Markdown } from './Markdown'

export interface TranscriptEntry {
  id: string
  kind: string
  ts: string
  text?: string
  stopReason?: string
  cost?: number
  isError?: boolean
  attachments?: string[]
}

interface Props {
  turn: TranscriptEntry[]
  isStreaming: boolean
  sessionId?: string
}

function formatTime(ts?: string): string {
  if (!ts) return ''
  const value = new Date(ts)
  if (Number.isNaN(value.getTime())) return ''
  return value.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TurnView({ turn, isStreaming, sessionId }: Props) {
  const terminal = turn.find((e) => e.kind === 'done' || e.kind === 'error')
  const complete = !!terminal
  const finalAssistant = [...turn].reverse().find((e) => e.kind === 'assistant_text' || e.kind === 'assistant')
  const user = turn[0]?.kind === 'user' ? turn[0] : null

  return (
    <article className="space-y-8">
      {user && <UserLine entry={user} sessionId={sessionId} />}

      {finalAssistant && (
        <AssistantBlock entry={finalAssistant} live={isStreaming && !complete} />
      )}

      {turn
        .filter((e) => e.kind === 'error')
        .map((e) => (
          <ErrorBlock key={e.id} />
        ))}
    </article>
  )
}

function UserLine({ entry, sessionId }: { entry: TranscriptEntry; sessionId?: string }) {
  return (
    <div className="flex flex-col items-end gap-2 animate-fade-in-right">
      <div className="flex items-center gap-2">
        <span className="text-xs text-mute">{formatTime(entry.ts)}</span>
        <span className="text-mute/40">·</span>
        <span className="font-display italic text-sm text-ember">You</span>
      </div>
      <div className="max-w-[85%] pl-5 border-l-[3px] border-ember/50">
        <p className="text-cream text-base leading-relaxed">
          {entry.text}
        </p>
        {entry.attachments && entry.attachments.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-3">
            {entry.attachments.map((path, i) => (
              <div key={i} className="flex flex-col gap-1">
                {sessionId ? (
                  <AuthImage
                    path={path}
                    sessionId={sessionId}
                    className="h-24 w-24 object-cover rounded-xl border border-haze/50"
                    alt={`Attachment ${i + 1}`}
                    enableLightbox
                  />
                ) : (
                  <a
                    href={`/uploads/${path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink-raised border border-haze/50 rounded-full text-xs text-mute hover:text-cream transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                    {path.split('/').pop()}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function AssistantBlock({
  entry,
  live,
}: {
  entry: TranscriptEntry
  live: boolean
}) {
  const text = entry.text || ''
  const hasText = text.trim().length > 0
  return (
    <div className="flex flex-col gap-2 animate-fade-in-left">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-leaf/70" />
        <span className="font-display italic text-sm text-cream">Beauty one-oh-one</span>
        <span className="text-mute/40">·</span>
        <span className="text-xs text-mute">{formatTime(entry.ts)}</span>
      </div>
      <div className="max-w-[90%] pl-5 border-l-[3px] border-haze/40">
        {hasText ? (
          <div className="text-cream text-base leading-relaxed">
            <Markdown text={text} />
            {live && <TypingIndicator />}
          </div>
        ) : (
          live && <TypingIndicator />
        )}
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <span className="inline-flex items-center gap-1 ml-1">
      <span className="inline-block w-1.5 h-1.5 bg-ember rounded-full animate-pulse" />
      <span className="inline-block w-1.5 h-1.5 bg-ember/70 rounded-full animate-pulse [animation-delay:150ms]" />
      <span className="inline-block w-1.5 h-1.5 bg-ember/40 rounded-full animate-pulse [animation-delay:300ms]" />
    </span>
  )
}

function ErrorBlock() {
  return (
    <div className="font-display italic text-sm text-ember border-l-[3px] border-ember pl-4 py-2 bg-ember/5 rounded-r-xl" role="alert">
      Something went wrong. Try rephrasing your question.
    </div>
  )
}
